import React, { useEffect, useMemo, useReducer } from 'react';

import { AccountType, CloudProviders } from '@ariksa/cloud-account';
import { Box, Flex, HStack, Radio, RadioGroup, Stack } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { customTheme } from 'theme';

import {
  Card,
  DottedUnderlineLink,
  FormFieldBox,
  PageHeaderWithIcon,
  StopSignLabel,
  WarningAlert,
} from 'components/DataDisplay';
import { Form, FormConfirmationField, Select } from 'components/DataEntry';
import { AwsIcon } from 'components/Icons';
import { renderRadioButton } from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/utils/utils';
import { radioBoxStyles } from 'containers/Setup/CloudAccounts/CloudAccountWizard/styles';
import { selectCloudAccounts } from 'containers/Setup/CloudAccounts/selectors';
import { actions } from 'containers/Setup/CloudAccounts/slice';
import { DataPolicyManagementHelp } from 'containers/Setup/CloudAccounts/types';

import {
  dataPolicyManagementHelpHeaderMapping,
  reviewDataAccessPermissionsMessage,
} from './utils';

export const EnableDataAccessManagementForAWS = () => {
  const dispatch = useDispatch();
  const {
    accountDetails,
    dataAccessManagement,
    identityProviderAccounts,
  } = useSelector(selectCloudAccounts);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const jitSetupPath = pathname.startsWith('/setup/jit')
    ? '/setup/jit'
    : '/jit/setup';

  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      isAutomatic: true,
      isConfirm: false,
      roleArn: '',
      externalId: '',
      regions: 'active',
      helpType: '',
      identityProvider: 'okta',
      oktaAccount: {},
      isConfirmed: false,
    },
  );

  const oktaAccountOptions = useMemo(
    () =>
      identityProviderAccounts.data.map(account => ({
        label: account.name,
        value: account.uuid,
      })),
    [identityProviderAccounts.data],
  );

  useEffect(() => {
    if (state.identityProvider !== 'okta') return;

    dispatch(
      actions.getIdentityProviderAccounts({
        q: {
          accountType: AccountType.IdentityProvider,
          cloudType: CloudProviders.Okta,
          page: 1,
          size: 1000,
        },
      }),
    );
  }, [dispatch, state.identityProvider]);

  useEffect(() => {
    if (state.oktaAccount?.value || !oktaAccountOptions.length) return;
    updateState({ oktaAccount: oktaAccountOptions[0] });
  }, [oktaAccountOptions, state.oktaAccount?.value]);

  const onSubmit = () => {
    if (!accountDetails.data?.uuid) return;

    dispatch(
      actions.enableDataAccessManagement({
        q: {
          dataAccessMgmtRequest: {
            account_id: accountDetails.data.uuid,
            identity_provider_type:
              state.identityProvider === 'okta'
                ? state.identityProvider
                : undefined,
            identity_provider_id:
              state.identityProvider === 'okta'
                ? state.oktaAccount?.value
                : undefined,
          },
        },
        onSuccess: () => navigate(jitSetupPath),
      }),
    );
  };

  return (
    <Card styles={{ cardContent: { pl: 32 } }}>
      <Form
        title={
          <PageHeaderWithIcon
            label="Enable Data Access Management"
            icon={<AwsIcon />}
            iconFilled={false}
          />
        }
        schema={{
          name: {
            type: 'custom-with-form-control',
            label: 'Connector name',
            component: (
              <FormFieldBox>
                {accountDetails.data?.name} (
                {accountDetails.data.cloud_account_id})
              </FormFieldBox>
            ),
          },
          identityProvider: {
            type: 'custom',
            component: () => (
              <Box {...radioBoxStyles}>
                <Stack>
                  <Stack spacing={0}>
                    <Box fontSize="md">
                      Which directory identity provider do you use
                    </Box>
                    <Box fontSize="sm" color={customTheme.colors.gray[250]}>
                      Please set up SCIM integration between your directory
                      identity provider and Snowflake
                    </Box>
                  </Stack>
                  <RadioGroup
                    value={state.identityProvider}
                    onChange={value => updateState({ identityProvider: value })}
                  >
                    <HStack spacing={10}>
                      <Radio value="okta" size="lg">
                        <Box fontSize="md">Okta</Box>
                      </Radio>
                      <Radio value="none" size="lg">
                        <Box fontSize="md">None</Box>
                      </Radio>
                    </HStack>
                  </RadioGroup>
                  {state.identityProvider === 'okta' && (
                    <Stack spacing={1}>
                      <Box fontSize="md">Okta Account</Box>
                      <Select
                        placeholder="Select Okta account"
                        options={oktaAccountOptions}
                        value={state.oktaAccount}
                        onChange={oktaAccount => updateState({ oktaAccount })}
                        isLoading={identityProviderAccounts.isLoading}
                      />
                    </Stack>
                  )}
                </Stack>
              </Box>
            ),
          },
          ...(state.identityProvider === 'okta'
            ? {
                confirm: {
                  type: 'custom',
                  component: () => (
                    <Stack>
                      <StopSignLabel label="To proceed, review carefully and perform each step indicated below" />
                      <Flex justify="flex-end">
                        <DottedUnderlineLink
                          onClick={() =>
                            navigate('/setup/integrations/add/okta')
                          }
                          label="Go to Setup"
                        />
                      </Flex>
                      <FormConfirmationField
                        header="Configure SCIM integration between Identity Provider and Ariksa"
                        subHeader="Ariksa uses this to manage data access control for users and groups in Identity Provider"
                        isChecked={state.isConfirmed}
                        onClick={() =>
                          updateState({ isConfirmed: !state.isConfirmed })
                        }
                      />
                    </Stack>
                  ),
                },
              }
            : {
                stopMessage: {
                  type: 'custom',
                  component: () => (
                    <StopSignLabel label="To proceed, review carefully and perform each step indicated below" />
                  ),
                },
              }),
          ...(!!state.isConfirmed
            ? {
                permissions: {
                  type: 'custom-with-form-control',
                  label: 'Setup account permissions for Ariksa',
                  component: (
                    <Box {...radioBoxStyles}>
                      <RadioGroup
                        value={state.isAutomatic ? 'Automatic' : 'Manual'}
                        onChange={value =>
                          updateState({ isAutomatic: value === 'Automatic' })
                        }
                      >
                        <HStack spacing={10}>
                          {renderRadioButton(
                            'Automatically',
                            'Create IAM role with necessary permissions',
                            'Automatic',
                          )}
                          {renderRadioButton(
                            'Manually',
                            'I’ll create the IAM role for Ariksa',
                            'Manual',
                          )}
                        </HStack>
                      </RadioGroup>
                    </Box>
                  ),
                },
                ...(!state.isAutomatic
                  ? {
                      confirmMessage: {
                        type: 'custom',
                        component: () =>
                          reviewDataAccessPermissionsMessage(
                            state.isConfirm,
                            val => updateState({ isConfirm: val }),
                          ),
                      },
                    }
                  : {}),
                ...(state.isConfirm && !state.isAutomatic
                  ? {
                      roleArn: {
                        type: 'text',
                        label: 'ARN of the IAM Role',
                        placeholder: 'Enter ARN of the IAM Role',
                        value: state.roleArn,
                        onChange: val => updateState({ roleArn: val }),
                      },
                      externalId: {
                        type: 'text',
                        label: 'External ID',
                        placeholder: 'Enter external ID',
                        value: state.externalId,
                        onChange: val => updateState({ externalId: val }),
                      },
                    }
                  : {}),
                region: {
                  type: 'custom-with-form-control',
                  label: 'Which regions do you want to enable',
                  helpLabel: `What's this`,
                  onClickHelpIcon: () =>
                    updateState({
                      helpType:
                        DataPolicyManagementHelp.RegionDataPolicyManagement,
                    }),
                  component: (
                    <Box {...radioBoxStyles}>
                      <RadioGroup
                        value={state.regions}
                        onChange={value => updateState({ regions: value })}
                      >
                        <HStack spacing={10}>
                          {renderRadioButton(
                            'Only active regions',
                            'We will enable only for regions with resources',
                            'active',
                          )}
                          {renderRadioButton(
                            'All regions',
                            'We will enable unused regions as well',
                            'all',
                          )}
                        </HStack>
                      </RadioGroup>
                    </Box>
                  ),
                },
                note: {
                  type: 'custom',
                  component: () => (
                    <Box w="full">
                      <WarningAlert
                        message={`You’ll be redirected to AWS console to complete the
                      enablement process. We establish a trust relationship with
                      the IAM role created in this account, that will be used to
                      create, update and delete data policies and resources to
                      provide granular access management`}
                      />
                    </Box>
                  ),
                },
              }
            : {}),
        }}
        helpDrawer={{
          isOpen: !!state.helpType,
          onClose: () => updateState({ helpType: '' }),
          content: dataPolicyManagementHelpHeaderMapping(state.helpType)
            ?.content,
          header: (
            <PageHeaderWithIcon
              label={
                dataPolicyManagementHelpHeaderMapping(state.helpType)?.header
              }
              icon={dataPolicyManagementHelpHeaderMapping(state.helpType)?.icon}
              fontWeight={600}
              fontSize="lg"
            />
          ),
          styles: { body: { pt: 6 } },
        }}
        buttonOptions={{
          submit: {
            name: 'ENABLE',
            isLoading: dataAccessManagement.isLoading,
            isDisabled:
              !accountDetails.data?.uuid ||
              (state.identityProvider === 'okta' &&
                (!state.oktaAccount?.value || !state.isConfirmed)) ||
              (!state.isAutomatic && (!state.roleArn || !state.externalId)),
          },
          reset: {
            isVisible: true,
            name: 'CANCEL',
            onClick: () => {
              navigate(jitSetupPath);
            },
          },
        }}
        handleSubmit={onSubmit}
        styles={{ form: { formWidth: '800px' } }}
      />
    </Card>
  );
};
